import { CommonInfoService } from './../../cart-process/common-info.service';
import { NotificationProviderService, NotificationProviderTypeEnum } from './../../external-interfaces/notification-provider.service';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { Injectable } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';
import { FormCustomValidators } from './form-custom-validators';
import { IdTypeEnum } from '../../models/cartDetails/idType.enum';

export interface ExtendedAbstractControl extends AbstractControl {
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class FormFieldsGeneratorService {
  public activeForm: FormGroup;
  public $activeFormObservable = new ReplaySubject<FormGroup>();
  public slotOfferForm = new FormGroup({
    number: new FormControl(
      '',
      [Validators.required, FormCustomValidators.mnpPhone]
    )});
  public userDataForm: FormGroup;
  public businessDataForm: FormGroup;
  public installationAddressForm: FormGroup = new FormGroup({});
  public correspondenceAddressForm: FormGroup = new FormGroup({});
  public customerDetailsForm: FormGroup;

  public hiddenGroups: string[] = [];
  public formFields: any;
  public modalForm: FormGroup;

  public deliveryAddress: FormGroup = new FormGroup({});
  public paymentOptions: FormGroup;
  public deliveryOptions: FormGroup;
  public selectedPos: FormGroup;

  constructor(private commonInfo: CommonInfoService,
              private notificationProvider: NotificationProviderService) {
  }

  public prepareIdentityConfirmation(type: string): void {
    if (!this.activeForm) {
      this.activeForm = new FormGroup({});
    }

    this.activeForm.addControl('identityConfirmationType', new FormControl(type, {validators: Validators.required}));
    (this.activeForm.controls.identityConfirmationType as ExtendedAbstractControl).name = 'identityConfirmationType';
    this.$activeFormObservable.next(this.activeForm);
  }

  prepareSelectedPos(items: string[]): void {
    this.selectedPos = new FormGroup({});

    items.forEach(item => {
      this.selectedPos.addControl(item, new FormControl(''));
      (this.selectedPos.controls[item] as ExtendedAbstractControl).name = item;
    });
  }

  preparePaymentOptions(): void {
    if (!this.activeForm) {
      this.activeForm = new FormGroup({});
    }
    this.activeForm.addControl('paymentMode', new FormControl('', {validators: Validators.required}));
    (this.activeForm.controls.paymentMode as ExtendedAbstractControl).name = 'paymentMode';
  }

  prepareDeliveryOptions(deliveryMode: string): void {
    if (!this.activeForm) {
      this.activeForm = new FormGroup({});
    }

    this.activeForm.addControl('shippingType', new FormControl(deliveryMode, {validators: Validators.required}));
    this.activeForm.addControl('shippingAddress', new FormControl(''));
    this.activeForm.addControl('courierNotes', new FormControl(''));
    (this.activeForm.controls.shippingType as ExtendedAbstractControl).name = 'shippingType';
    (this.activeForm.controls.shippingAddress as ExtendedAbstractControl).name = 'shippingAddress';
    (this.activeForm.controls.courierNotes as ExtendedAbstractControl).name = 'courierNotes';

    this.$activeFormObservable.next(this.activeForm);
  }

  prepareDeliveryAddress(fields): void {
    this.generateFormFields(this.activeForm, fields);

    this.deliveryAddress = (this.activeForm.controls.deliveryAddress as FormGroup);
    this.toggleExpandedFormGroupVisibility('deliveryAddress', this.activeForm, true);
  }

  public watchUserData(): void {
    if (this.commonInfo.isSecondPage) {
      this.createDefaultFormGroups();

      this.notificationProvider.subscribe(NotificationProviderTypeEnum.FORM_TEMP_FIELDS_CONFIGURATION, data => {

        if (!this.businessDataForm || !this.userDataForm) {
          this.createDefaultFormGroups();
        }

        if (!this.activeForm && data) {
          this.createFormGroups(data);
        }
      });
    }
  }

  addInstallationAddressFormControl(): void {
    this.businessDataForm.addControl('installationAddressOption', new FormControl('off', {}));
    this.userDataForm.addControl('installationAddressOption', new FormControl('off', {}));
  }

  public createFormGroups(fields: {b2b: any, b2c: any, common: any}): void {
    const businessForm = Object.assign({...fields.b2b, ...fields.common});
    const userForm = Object.assign({...fields.b2c, ...fields.common});

    this.generateFormFields(this.businessDataForm, businessForm);
    this.generateFormFields(this.userDataForm, userForm);
    this.hiddenGroups = [...new Set(this.hiddenGroups)];

    this.hiddenGroups.forEach(groupName => {
      switch (groupName) {
        case 'installationAddress':
          this.installationAddressForm = this.userDataForm.controls.installationAddress as FormGroup;
          break;
        case 'correspondenceAddress':
          this.correspondenceAddressForm = this.userDataForm.controls.correspondenceAddress as FormGroup;
          break;
      }
      });

    if (this.hiddenGroups) {
      this.hiddenGroups.forEach(group => this.toggleExpandedFormGroupVisibility(group, this.userDataForm, true));
      this.hiddenGroups.forEach(group => this.toggleExpandedFormGroupVisibility(group, this.businessDataForm, true));
    }
  }

  public generateFormFields(form: FormGroup, fields: any): void {
    for (const fieldName in fields) {
      if (fields[fieldName].fields ) {
        const fieldsNames = Object.keys(fields[fieldName].fields);
        fieldsNames.forEach((field) =>  {
          if (fields[fieldName].forceHide) {
            this.hiddenGroups.push(fieldName);
          }
          this.generateFormControl(form, fieldName, fields[fieldName].fields[field]);
        });
      } else {
        if (fields[fieldName].forceHide) {
          this.hiddenGroups.push(fieldName);
        }

        this.generateFormControl(form, fields[fieldName].input.formControlGroup, fields[fieldName]);
      }
    }

    if (!fields.correspondenceAddress && form.controls.mailingAddressOption) {
      form.removeControl('mailingAddressOption');
    }
  }

  generateFormControl(form: FormGroup, group: string, control: any): void {
    let fieldsGroup = form?.get(group) as FormGroup;

    if (!fieldsGroup) {
      form.addControl(group, new FormGroup({}));
      fieldsGroup = form.get(group) as FormGroup;
    }

    fieldsGroup?.addControl(control.input.formControlName,
      new FormControl('', {validators: control.input._validators}));
    (fieldsGroup.controls[control.input.formControlName] as ExtendedAbstractControl).name = control.input.name;
  }


  public createDefaultFormGroups(): void {
    this.businessDataForm = new FormGroup({
      idCardType: new FormControl('', {validators: Validators.required}),
      mailingAddressOption: new FormControl('off', {validators: Validators.required}),
    });

    this.userDataForm = new FormGroup({
      idCardType: new FormControl('', {validators: Validators.required}),
      mailingAddressOption: new FormControl('off', {validators: Validators.required}),
    });

    (this.businessDataForm.controls.idCardType as ExtendedAbstractControl).name = 'idCardType';
    (this.userDataForm.controls.idCardType as ExtendedAbstractControl).name = 'idCardType';
    (this.businessDataForm.controls.mailingAddressOption as ExtendedAbstractControl).name = 'mailingAddressOption';
    (this.userDataForm.controls.mailingAddressOption as ExtendedAbstractControl).name = 'mailingAddressOption';
  }

  public createCustomerDetailsForm(email: string, phone: string): void {
    this.customerDetailsForm = new FormGroup({
      customerEmail: new FormControl(email, [Validators.required, FormCustomValidators.email]),
      customerPhone: new FormControl(phone, [Validators.required, FormCustomValidators.phone])
    });

    (this.customerDetailsForm.controls.customerEmail as ExtendedAbstractControl).name = 'customerEmail';
    (this.customerDetailsForm.controls.customerPhone as ExtendedAbstractControl).name = 'customerPhone';

    this.activeForm = this.customerDetailsForm;
    this.$activeFormObservable.next(this.activeForm);
  }


  public setFirstStepActiveForm(): void {
    this.activeForm = this.slotOfferForm;
    this.$activeFormObservable.next(this.activeForm);
  }

  public setSecondStepActiveForm(b2c: boolean): void {
    if (this.activeForm) {
      if (b2c) {
        this.mergeActiveForm(this.userDataForm);
      } else {
        this.mergeActiveForm(this.businessDataForm);
      }
    }

    this.activeForm = b2c ? this.userDataForm : this.businessDataForm;
    this.$activeFormObservable.next(this.activeForm);
  }

  mergeActiveForm(form: FormGroup): void {
    (Object as any).keys(form?.controls).forEach((key: string) => {
      if (!!(form.controls[key] as FormGroup)?.controls) {
        const childControls = (form.controls[key] as FormGroup).controls;
        (Object as any).keys(childControls).forEach((childKey: string) => {
          const activeFormControls = (this.activeForm.controls[key] as FormGroup)?.controls[childKey];

          if (!!activeFormControls?.value) {
            (form.controls[key] as FormGroup).controls[childKey].setValue(activeFormControls.value);
            (form.controls[key] as FormGroup).controls[childKey].markAsDirty();
          }
        });
      } else {
        if (!!this.activeForm.controls[key]?.value) {
          form.controls[key].setValue(this.activeForm.controls[key].value);
          form.controls[key].markAsDirty();

          if (key === 'mailingAddressOption' || key === 'installationAddressOption') {
            const formGroup = key === 'mailingAddressOption' ? 'correspondenceAddress' : 'installationAddress';
            this.toggleExpandedFormGroupVisibility(formGroup, form, this.activeForm.controls[key].value === 'off', this.activeForm.controls[key].value === 'on');
          }
        }
      }
    });
  }

  updateFormByIdType(idType: IdTypeEnum): void {
    const documentNo = this.activeForm.controls.customerDetails.get('documentNo');
    const documentExpiryDate = this.activeForm.controls.customerDetails.get('idExpiryDate');
    const mDocumentNo = this.activeForm.controls.customerDetails.get('mDocumentNo');
    const mDocumentExpiryDate = this.activeForm.controls.customerDetails.get('mIdExpiryDate');

    if (idType === IdTypeEnum.ID_CARD) {
      if (mDocumentNo) {
        mDocumentNo.clearValidators();
        mDocumentExpiryDate.clearValidators();
      }

      if (documentNo) {
        documentNo.setValidators([Validators.required, FormCustomValidators.personalIdNumber]);
        documentExpiryDate.setValidators([Validators.required, FormCustomValidators.validateExpiryDate]);
      }
    } else {
      if (documentNo) {
        documentNo.clearValidators();
        documentExpiryDate.clearValidators();
      }

      if (mDocumentNo) {
        mDocumentNo.setValidators([Validators.required, FormCustomValidators.mPersonalIdNumber]);
        mDocumentExpiryDate.setValidators([Validators.required, FormCustomValidators.validateExpiryDate]);
      }
    }

    if (documentNo) {
      documentNo.updateValueAndValidity();
      documentExpiryDate.updateValueAndValidity();
    }

    if  (mDocumentNo) {
      mDocumentExpiryDate.updateValueAndValidity();
      mDocumentNo.updateValueAndValidity();
    }
  }

  public setModalForm(form: FormGroup): void {
    this.modalForm = form;
  }

  public watchActiveForm(): Observable<FormGroup> {
    return this.$activeFormObservable.asObservable();
  }

  public getActiveForm(): FormGroup {
    return this.activeForm;
  }

  public resetActiveForm(): void {
    this.activeForm = new FormGroup({});
  }

  public checkActiveFormValidation(formGroup?: FormGroup): void {
    const activeForm = formGroup ? formGroup : this.activeForm;

    if (!activeForm || !activeForm.controls) {
      return;
    }

    activeForm.markAllAsTouched();

    (Object as any).values(activeForm.controls).forEach((control: FormGroup) => {
      control.markAsDirty({ onlySelf: true });
      control.markAsTouched();
      control.updateValueAndValidity();

      if (!!control.controls) {
        (Object as any).values(control.controls).forEach((c: FormGroup) => {
          this.checkActiveFormValidation(c);
        });
      }
    });
  }

  public toggleExpandedFormGroupVisibility(groupName: string, form?: FormGroup, forceHide = false, forceOpen = false): void {
    const formGroup = form ? form : this.activeForm;
    const groupNameVisible =  formGroup?.get(groupName);
    let childFormGroup;

    switch (groupName) {
      case 'installationAddress':
        childFormGroup = this.installationAddressForm;
        if (this.activeForm?.controls.installationAddressOption?.value === 'on') {
          forceHide = false;
          forceOpen = true;
        }
        break;
      case 'correspondenceAddress':
        childFormGroup = this.correspondenceAddressForm;
        if (this.activeForm?.controls.mailingAddressOption?.value === 'on') {
          forceHide = false;
          forceOpen = true;
        }
        break;
      case 'deliveryAddress':
        childFormGroup = this.deliveryAddress;
        break;
      case 'selectedPos':
        childFormGroup = this.selectedPos;
        break;
      case 'shippingAddress':
        childFormGroup = new FormControl('', {validators: Validators.required});
        break;
    }


    if (groupNameVisible && !forceOpen) {
      formGroup?.removeControl(groupName);
    } else if (!forceHide) {
      formGroup?.addControl(groupName, childFormGroup);
    }
    this.$activeFormObservable.next(this.activeForm);
  }
}
